import React, { useContext, useMemo } from 'react'

import PropTypes from 'prop-types'

import { Create as CreateIcon, FilterList as FilterIcon } from '@mui/icons-material'
import { Paper, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'

import { annotationTypeMap } from '~/src/Annotations/constants'
import { JournalContext } from '~/src/Journal/utils'
import { idPropType } from '~/src/Lib/PropTypes'
import { formatList } from '~/src/Lib/Utils'

const NeverEntries = React.memo(({ centerEverything }) => (
  <Paper square data-testid="journal-no-entries" style={centerEverything}>
    <CreateIcon color="primary" style={{ fontSize: 72 }} />
    <Typography variant="subtitle1" color="textPrimary" gutterBottom>
      The journal contains a chronological list of annotations, manual measurements, tasks, targets and alerts for this facility.
    </Typography>
    <Typography variant="body1">
      To get started, click the plus button in the left navigation or on the room dashboard chart.
    </Typography>
  </Paper>
))

const NoEntriesComponent = ({
  centerEverything,
  children,
  isFiltered,
  activeTypes,
  allowedTypes,
  activeUser = null,
  journal,
}) => {
  const { before, after, items } = journal

  if (!(before + after + (items?.length ?? 0)) && !isFiltered) {
    return <NeverEntries />
  }

  return (
    <>
      {children}
      <Paper square data-testid="journal-no-matching-entries" style={centerEverything}>
        <div style={{ width: '100%', textAlign: 'center' }}>
          <FilterIcon color="primary" style={{ fontSize: 72 }} />
        </div>
        <Typography variant="subtitle1" color="textPrimary" gutterBottom>
          There are no
          {activeTypes !== allowedTypes ? ` ${formatList(activeTypes.map(t => {
            const { [t]: type } = annotationTypeMap
            return type.textLabel ?? type.label ?? type.type
          }), 'or')} ` : ' '}
          entries
          {activeUser ? ` for ${activeUser.fullName} ` : ' '}
          in the journal.
        </Typography>
      </Paper>
    </>
  )
}
NoEntriesComponent.propTypes = {
  centerEverything: PropTypes.objectOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ])).isRequired,
  children: PropTypes.element.isRequired,
  isFiltered: PropTypes.bool.isRequired,
  activeTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  allowedTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  activeUser: PropTypes.shape({ id: idPropType, fullName: PropTypes.string }),
  journal: PropTypes.shape({
    after: PropTypes.number,
    before: PropTypes.number,
    items: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
}
NoEntriesComponent.displayName = 'Journal/NoEntries'

export default React.memo(props => {
  const { activeUser, activeTypes, allowedTypes, selectedDate } = useContext(JournalContext)
  const theme = useTheme()
  const { palette: colors } = theme

  const centerEverything = useMemo(() => ({
    display: 'flex',
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center',
    flexWrap: 'wrap',
    height: '100%',
    padding: 16,
    backgroundColor: colors.background.dark,
  }), [colors.background.dark])

  return (
    <NoEntriesComponent
      {...props}
      centerEverything={centerEverything}
      isFiltered={activeUser || (activeTypes?.length !== allowedTypes?.length)}
      activeUser={activeUser}
      allowedTypes={allowedTypes}
      selectedDate={selectedDate}
    />
  )
})
